import type React from 'react';
import { useId } from 'react';

import { AdornedInput, FieldSet, FormField, Label, LabeledControl, Legend, Radio } from '@zillow/constellation-10';

import type { Section } from '../../../models/index';
import YesAndNoExpansion from '../../../components/Widgets/YesAndNoExpansion';
import { INSUFFICIENT_FUNDS_FEE } from '../../../constants/index';
import InlineTooltip from '../../../components/Widgets/InlineTooltip';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

// Third iteration of Insufficient Funds fee which adds a radio selection between a flat fee
// and a associated fee.
export default function InsufficientFundsFee_v3({ section, setSectionValues }: Props) {
  const radioGroupName = useId();
  const associatedFeeId = useId();
  const flatFeeId = useId();

  function disableInsufficientFundsFee(): void {
    setSectionValues({
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.IS_ENABLED]: false,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.ACTUAL_COST]: false,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE]: false,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE_AMOUNT]: '',
    });
  }

  function enableInsufficientFundsFee(): void {
    setSectionValues({
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.IS_ENABLED]: true,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE_AMOUNT]: '',
    });
  }

  function onAssociatedFeeClick(): void {
    setSectionValues({
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.ACTUAL_COST]: true,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE]: false,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE_AMOUNT]: '',
    });
  }

  function onFlatFeeClick(): void {
    setSectionValues({
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.ACTUAL_COST]: false,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE]: true,
    });
  }

  function onAmountChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE_AMOUNT]: value,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.ACTUAL_COST]: false,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE]: true,
    });
  }

  const insufficientFundEnabled = section.getFieldValue(INSUFFICIENT_FUNDS_FEE.FIELD_NAME.IS_ENABLED);

  return (
    <FormField
      required
      label={
        <>
          <Label>
            Will you charge a fee if a payment gets{' '}
            <InlineTooltip
              tooltipBodyText="Reversals are usually caused by checks being returned for insufficient funds or by the tenant stopping or
          canceling a check. A fee charged for reversal of payment is different from a late fee."
            >
              reversed
            </InlineTooltip>
            ?
          </Label>
        </>
      }
      control={
        <YesAndNoExpansion
          yesHandler={enableInsufficientFundsFee}
          noHandler={disableInsufficientFundsFee}
          yesActive={insufficientFundEnabled}
        >
          <FieldSet required legend={<Legend>What type of fee will the tenant pay?</Legend>}>
            <LabeledControl
              controlId={associatedFeeId}
              label={<Label>Pay actual cost incurred by landlord</Label>}
              disabled={section.isFieldDisabled(INSUFFICIENT_FUNDS_FEE.FIELD_NAME.ACTUAL_COST)}
              control={
                <Radio
                  name={radioGroupName}
                  checked={section.getFieldValue(INSUFFICIENT_FUNDS_FEE.FIELD_NAME.ACTUAL_COST)}
                  onChange={() => onAssociatedFeeClick()}
                />
              }
            />
            <LabeledControl
              controlId={flatFeeId}
              label={<Label>Flat fee</Label>}
              control={
                <Radio
                  name={radioGroupName}
                  checked={section.getFieldValue(INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE)}
                  onChange={() => onFlatFeeClick()}
                />
              }
            />
            <FormField
              required
              css={{ marginLeft: 'lg' }}
              control={
                <AdornedInput.Root>
                  <AdornedInput.Adornment>$</AdornedInput.Adornment>
                  <AdornedInput.Input
                    type="text"
                    inputMode="decimal"
                    aria-labelledby="fee-amount"
                    value={section.getFieldValue(INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE_AMOUNT)}
                    onBlur={onAmountChange}
                    onChange={onAmountChange}
                    onFocus={onFlatFeeClick}
                  />
                  <AdornedInput.Adornment>flat fee</AdornedInput.Adornment>
                </AdornedInput.Root>
              }
            />
          </FieldSet>
        </YesAndNoExpansion>
      }
    />
  );
}
